import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";

import {
    NAME_LARGE_RES,
    NAME_FULL_RES,
    NAME_BASIC_RES,
    NAME_STANDARD,
    NAME_PREMIUM,
    getLicenseSchema
} from "photos/helpers";
import { TRACK_ITEM_PHOTO, trackViewPurchaseOptions } from "core/trackEvent";

import {
    Title,
    PhotoName,
    Buttons,
    ContinueButton,
    CancelButton,
    Note
} from "./styles";
import { useCurrentUser } from "js/contexts/CurrentUserContext";
import Drawer from "core/Drawer";
import colors from "core/colors";
import CloseIcon from "core/ReusableIcons/Close";
import PhotoIcon from "core/ReusableIcons/Photo";
import WarningIcon from "core/ReusableIcons/Warning";
import LicenseButton from "core/LicenseButton";
import Heading from "core/Heading";

import useFeatureFlag from "frontend/hooks/useFeatureFlag";
import { PREMIUM_PHOTOS_FLAG } from "frontend/data/featureFlags";

const PhotoDetailDrawer = ({ photo, onClose, onSubmit, initialSelection }) => {
    const [selected, setSelected] = useState(initialSelection);
    const currentUser = useCurrentUser();
    const proUser = !!(currentUser && currentUser.hasSubscription);
    const prepaidUser = !!(currentUser && currentUser.hasPrepaidBalance);
    const hasPremiumPhotos = useFeatureFlag(PREMIUM_PHOTOS_FLAG);

    const onSubmitClick = useCallback(() => {
        onSubmit(selected);
    }, [selected, onSubmit]);

    const licenseProps = { photo, selected, setSelected };

    const paidSchemas = hasPremiumPhotos
        ? [
              getLicenseSchema(
                  photo.isPremium ? NAME_PREMIUM : NAME_STANDARD,
                  licenseProps
              )
          ]
        : [
              getLicenseSchema(NAME_LARGE_RES, licenseProps),
              getLicenseSchema(NAME_FULL_RES, licenseProps)
          ];
    const licenseSchemas = [
        paidSchemas,
        [getLicenseSchema(NAME_BASIC_RES, licenseProps)]
    ];

    useEffect(() => {
        trackViewPurchaseOptions({
            itemId: photo.id,
            itemName: photo.title,
            itemType: TRACK_ITEM_PHOTO
        });
    }, []);

    return (
        <Drawer onClose={onClose} closeLabel="Cancel license selection">
            <Title>
                <Heading type="h1" size="sm">
                    Get This Photo
                </Heading>
                <button aria-label="Close" onClick={onClose}>
                    <CloseIcon />
                </button>
            </Title>
            <PhotoName>
                <PhotoIcon />
                <p>{photo.title}</p>
            </PhotoName>
            {licenseSchemas.map((schemas, i) => (
                <LicenseButton key={i} type="photo" schemas={schemas} />
            ))}
            <Buttons>
                <ContinueButton
                    onClick={onSubmitClick}
                    data-testid="photo-drawer-continue"
                >
                    Continue
                </ContinueButton>
                <CancelButton
                    onClick={onClose}
                    data-testid="photo-drawer-cancel"
                >
                    Cancel
                </CancelButton>
            </Buttons>
            {proUser && (
                <Note data-testid="subscription-photo-picker-warning">
                    <WarningIcon fillColor={colors.accent.lightMode.pansy} />
                    <span>
                        Please note, NounPro Icon Subscriptions can't be used
                        for photo purchases.
                    </span>
                </Note>
            )}
            {prepaidUser && (
                <Note data-testid="prepaid-photo-picker-warning">
                    <WarningIcon fillColor={colors.accent.lightMode.pansy} />
                    <span>
                        Please note, currently Prepaid can't be used towards
                        photo purchases.
                    </span>
                </Note>
            )}
        </Drawer>
    );
};

PhotoDetailDrawer.propTypes = {
    photo: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    initialSelection: PropTypes.string.isRequired
};

export default PhotoDetailDrawer;
